import { useEffect } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline'
import { useHelpContent } from '../../hooks/useHelpContent'
import { useAuth } from '../../hooks/useAuth'

/**
 * Extract the first chunk of plain text from react-markdown's blockquote children
 * so we can decide whether the block is a "Coach tip" callout.
 */
function firstText(node) {
  if (typeof node === 'string') {
    // Skip whitespace-only text nodes that react-markdown sprinkles between
    // element children (e.g. newlines inside a blockquote).
    return node.trim() ? node : ''
  }
  if (Array.isArray(node)) {
    for (const child of node) {
      const t = firstText(child)
      if (t) return t
    }
    return ''
  }
  if (node && typeof node === 'object' && node.props) {
    return firstText(node.props.children)
  }
  return ''
}

export default function HelpContent({ section }) {
  const { t, i18n } = useTranslation()
  const { user } = useAuth()
  const navigate = useNavigate()

  // Resolve the active language, stripping region tags ('pt-BR' stays but
  // 'de-CH' becomes 'de') so markdown filenames stay short.
  const rawLang = i18n.language || 'en'
  const lang = rawLang === 'pt-BR' ? 'pt-BR' : rawLang.split('-')[0]

  const { loading, content, usedFallback, notFound } = useHelpContent(section, lang)

  // Non-admin users hitting /help/demo-environment bounce back to the TOC
  // landing page. The markdown file itself is harmless (no secrets), but
  // this keeps the URL space honest: non-admins don't have the section.
  useEffect(() => {
    if (section === 'demo-environment' && user && user.role !== 'super_user') {
      navigate('/help/quick-start', { replace: true })
    }
  }, [section, user, navigate])


  const components = {
    // Intercept internal links, open externals in a new tab safely.
    a({ href, children, ...rest }) {
      if (!href) {
        return <a {...rest}>{children}</a>
      }
      if (href.startsWith('/')) {
        return (
          <a
            href={href}
            onClick={(e) => {
              e.preventDefault()
              navigate(href)
            }}
            className="text-primary-600 hover:underline"
          >
            {children}
          </a>
        )
      }
      if (href.startsWith('http')) {
        return (
          <a
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            className="text-primary-600 hover:underline"
          >
            {children}
          </a>
        )
      }
      return (
        <a href={href} className="text-primary-600 hover:underline">
          {children}
        </a>
      )
    },

    // Coach tip callout: any blockquote whose first text starts with
    // "Coach tip:" gets the yellow-callout treatment. Everything else
    // renders as a plain blockquote.
    blockquote({ children }) {
      const text = firstText(children).trim()
      if (text.startsWith('Coach tip:')) {
        return (
          <div
            role="note"
            data-testid="coach-tip"
            className="bg-yellow-50 border-l-4 border-yellow-400 p-3 my-3 flex gap-2"
          >
            <ExclamationTriangleIcon
              className="h-5 w-5 text-yellow-600 flex-shrink-0 mt-0.5"
              aria-hidden="true"
            />
            <div className="text-sm text-yellow-900 [&>p]:m-0">{children}</div>
          </div>
        )
      }
      return (
        <blockquote className="border-l-4 border-gray-300 pl-4 italic text-gray-700 my-3">
          {children}
        </blockquote>
      )
    },

    h1({ children }) {
      return <h1 className="text-2xl font-bold text-gray-900 mt-2 mb-4">{children}</h1>
    },
    h2({ children }) {
      return <h2 className="text-xl font-semibold text-gray-900 mt-6 mb-3">{children}</h2>
    },
    h3({ children }) {
      return <h3 className="text-lg font-semibold text-gray-900 mt-4 mb-2">{children}</h3>
    },
    p({ children }) {
      return <p className="text-gray-800 my-3 leading-relaxed">{children}</p>
    },
    ul({ children }) {
      return <ul className="list-disc list-inside my-3 space-y-1 text-gray-800">{children}</ul>
    },
    ol({ children }) {
      return <ol className="list-decimal list-inside my-3 space-y-1 text-gray-800">{children}</ol>
    },
    code({ inline, children }) {
      if (inline) {
        return <code className="bg-gray-100 text-gray-800 px-1 py-0.5 rounded text-sm font-mono">{children}</code>
      }
      return (
        <pre className="bg-gray-100 text-gray-800 p-3 rounded text-sm font-mono overflow-x-auto my-3">
          <code>{children}</code>
        </pre>
      )
    },
  }

  if (loading) {
    return (
      <div className="animate-pulse space-y-3" aria-busy="true">
        <div className="h-6 w-1/3 bg-gray-200 rounded" />
        <div className="h-4 w-full bg-gray-200 rounded" />
        <div className="h-4 w-5/6 bg-gray-200 rounded" />
        <div className="h-4 w-2/3 bg-gray-200 rounded" />
      </div>
    )
  }

  if (notFound) {
    return (
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
        <p className="text-gray-800 mb-3">{t('help.sectionNotFound')}</p>
        <Link to="/help/quick-start" className="text-primary-600 hover:underline">
          {t('help.toc.sections.quickStart')}
        </Link>
      </div>
    )
  }

  return (
    <article className="prose prose-sm md:prose-base max-w-none">
      {usedFallback && (
        <div
          role="note"
          data-testid="help-fallback-notice"
          className="bg-blue-50 text-blue-800 px-3 py-2 rounded text-sm mb-4"
        >
          {t('help.translationMissing')}
        </div>
      )}
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>
        {content || ''}
      </ReactMarkdown>
    </article>
  )
}
